class AtdVideoScroller extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    this.dotsContainer = this.querySelector('[data-dots]');
    this.cards = Array.from(this.querySelectorAll('.atd-video-card'));
    this.videos = Array.from(this.querySelectorAll('[data-atd-video]'));

    this.buildDots();
    this.bindEvents();
    this.updateActiveDot();
    this.updateControls();

    // Recalcular si cambia el ancho (rotación, resize, theme editor).
    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.track);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  // Oculta flechas + puntos cuando los videos caben completos (no hay scroll
  // que recorrer). Funciona en escritorio y móvil porque mide el desbordamiento
  // real del track, no el número de tarjetas configurado.
  updateControls() {
    if (!this.track) return;
    const isScrollable = this.track.scrollWidth - this.track.clientWidth > 2;
    this.classList.toggle('atd-video-scroller1--static', !isScrollable);
  }

  buildDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.innerHTML = '';
    this.cards.forEach((_, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'atd-video-scroller1__dot';
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-label', `Ir al video ${i + 1}`);
      btn.addEventListener('click', () => this.goToCard(i));
      this.dotsContainer.appendChild(btn);
    });
    this.dots = Array.from(this.dotsContainer.children);
  }

  goToCard(index) {
    const card = this.cards[index];
    if (!card || !this.track) return;
    const offset = card.offsetLeft - this.track.offsetLeft;
    this.track.scrollTo({ left: offset, behavior: 'smooth' });
  }

  updateActiveDot() {
    if (!this.dots || !this.dots.length || !this.track) return;
    const scrollLeft = this.track.scrollLeft;
    let activeIndex = 0;
    let minDiff = Infinity;
    this.cards.forEach((card, i) => {
      const diff = Math.abs(card.offsetLeft - this.track.offsetLeft - scrollLeft);
      if (diff < minDiff) {
        minDiff = diff;
        activeIndex = i;
      }
    });
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === activeIndex));
  }

  bindEvents() {
    this.prevBtn?.addEventListener('click', () => this.scrollByCard(-1));
    this.nextBtn?.addEventListener('click', () => this.scrollByCard(1));

    this.querySelectorAll('[data-atd-play]').forEach((btn, i) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.play(i);
      });
    });

    // Click sobre el video pausa
    this.videos.forEach((video, i) => {
      const card = video.closest('.atd-video-card');
      video.addEventListener('click', () => {
        if (!video.paused) {
          video.pause();
        } else {
          this.play(i);
        }
      });
      video.addEventListener('pause', () => card?.classList.remove('is-playing'));
      video.addEventListener('ended', () => card?.classList.remove('is-playing'));
    });

    this.track?.addEventListener('scroll', () => this.updateActiveDot(), { passive: true });
  }

  scrollByCard(direction) {
    if (!this.track) return;
    const card = this.track.querySelector('.atd-video-card');
    if (!card) return;
    const gap = parseFloat(getComputedStyle(this.track).gap || '0');
    const step = card.offsetWidth + gap;
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const maxScroll = scrollWidth - clientWidth;

    // Loop infinito: si estás en el extremo, salta al opuesto
    if (direction > 0 && scrollLeft >= maxScroll - 2) {
      this.track.scrollTo({ left: 0, behavior: 'smooth' });
    } else if (direction < 0 && scrollLeft <= 2) {
      this.track.scrollTo({ left: maxScroll, behavior: 'smooth' });
    } else {
      this.track.scrollBy({ left: step * direction, behavior: 'smooth' });
    }
  }

  play(index) {
    this.videos.forEach((video, i) => {
      const card = video.closest('.atd-video-card');
      if (i !== index) {
        video.pause();
        card?.classList.remove('is-playing');
      }
    });
    const video = this.videos[index];
    if (!video) return;
    video.closest('.atd-video-card')?.classList.add('is-playing');
    video.play().catch(() => {
      video.muted = true;
      video.play();
    });
  }
}

if (!customElements.get('atd-video-scroller')) {
  customElements.define('atd-video-scroller', AtdVideoScroller);
}
